import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, View } from 'react-native';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { ListContainerNew } from '../../../common/containers';

import { Dialog, TextInput } from '../../components';
import { ImageViewer } from '../../components/image';
import { PaginationNav } from '../../components/pagination';
import Text from '../../components/text/Text';
import { PrimaryButton, SectionTitle } from '../../design/components';
import { ListContext } from '../../containers';
import { withErrorAndNoDataComponents } from '../../containers/helpersListContainer';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { app as appActions } from '../../actions';

import MediaListItem from './MediaListItem';

/**
 * Photos of a visit (or a machine): "Taken this visit (n)" with each photo,
 * then photos still saved on the phone (`queuedPhotos`: { id, link, syncTag }).
 * `emptyMessage` shows when there are none.
 */
const MediaListRaw = ({
	modifyItem: modifyMediaWithoutId,
	openDialog,
	closeDialog,
	setRefetch,
	queuedPhotos = [],
	emptyMessage,
}) => {
	const styles = useThemedStyles(createStyles);
	const [currentPhotoIndex, setCurrentPhotoIndex] = useState(null);

	const {
		items,
		refetch,
		count,
		changePage,
		variables: { page, perPage } = {},
	} = useContext(ListContext);

	useEffect(() => {
		if (setRefetch && refetch) {
			setRefetch(() => refetch);
		}
	}, [refetch, setRefetch]);

	const onClickAddComment = item => {
		openDialog(() => (
			<AddCommentDialog
				modifyMediaWithoutId={modifyMediaWithoutId}
				item={item}
				closeDialog={closeDialog}
			/>
		));
	};
	const photos = items || [];
	const total = (count || photos.length) + queuedPhotos.length;
	const customProps = { setCurrentPhotoIndex, onClickAddComment };

	return (
		<View style={styles.list}>
			<SectionTitle title={`Taken this visit (${total})`} />
			{photos.map((media, index) => (
				<MediaListItem key={media.id} item={media} index={index} customProps={customProps} />
			))}
			<QueuedPhotos queuedPhotos={queuedPhotos} />
			{!total && Boolean(emptyMessage) && (
				<View style={styles.empty}>
					<Text style={styles.emptyText}>{emptyMessage}</Text>
				</View>
			)}
			{count > photos.length && (
				<PaginationNav count={count} page={page} perPage={perPage} onPageChange={changePage} />
			)}

			<Modal
				visible={currentPhotoIndex !== null}
				transparent={true}
				onRequestClose={() => setCurrentPhotoIndex(null)}
			>
				<ImageViewer
					close={() => setCurrentPhotoIndex(null)}
					index={currentPhotoIndex}
					imageUrls={photos.map(media => ({ url: media.link }))}
				/>
			</Modal>
			<Dialog />
		</View>
	);
};

const QueuedPhotos = ({ queuedPhotos }) => queuedPhotos.map(photo => (
	<MediaListItem key={photo.id} item={photo} syncTag={photo.syncTag} />
));

// The list's error and no-data states replace the whole list (e.g. offline
// with nothing cached); photos saved on the phone stay visible under them
// with their sync status.
const withQueuedPhotoStates = WrappedComponent => function MediaListStates (props) {
	const { ErrorComponent, NoDataComponent, queuedPhotos = [] } = props;
	const styles = useThemedStyles(createStyles);
	const withQueuedPhotos = useCallback(StateComponent => function StateWithQueuedPhotos () {
		if (!queuedPhotos.length) {
			return <StateComponent />;
		}

		return (
			<View style={styles.list}>
				<StateComponent />
				<SectionTitle title={`Taken this visit (${queuedPhotos.length})`} />
				<QueuedPhotos queuedPhotos={queuedPhotos} />
			</View>
		);
	}, [queuedPhotos, styles.list]);
	const QueuedErrorComponent = useMemo(() => withQueuedPhotos(ErrorComponent), [ErrorComponent, withQueuedPhotos]);
	const QueuedNoDataComponent = useMemo(() => withQueuedPhotos(NoDataComponent), [NoDataComponent, withQueuedPhotos]);

	return <WrappedComponent {...props} ErrorComponent={QueuedErrorComponent} NoDataComponent={QueuedNoDataComponent} />;
};

const AddCommentDialog = ({
	modifyMediaWithoutId,
	item,
	closeDialog,
}) => {
	const styles = useThemedStyles(createStyles);
	const [comment, setComment] = useState('');

	return (
		<View style={styles.dialog}>
			<Text style={styles.dialogLabel}>Comment</Text>
			<TextInput
				multiline={true}
				numberOfLines={4}
				style={styles.commentInput}
				value={comment}
				onChange={setComment}
				accessibilityLabel="Comment"
			/>
			<PrimaryButton
				label="Add comment"
				onPress={() => {
					modifyMediaWithoutId({
						id: item.id,
						comment,
					});
					closeDialog();
				}}
				disabled={!comment}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	list: {
		gap: space.sm,
	},
	empty: {
		padding: space.lg,
		borderRadius: radius.card,
		backgroundColor: colors.card,
	},
	emptyText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	dialog: {
		gap: space.md,
	},
	dialogLabel: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	commentInput: {
		minHeight: 100,
		textAlignVertical: 'top',
	},
});

const mapDispatchToProps = dispatch => ({
	openDialog: component => dispatch(appActions.openDialog(component)),
	closeDialog: () => dispatch(appActions.closeDialog()),
});

// As ListContainer, with the queued photos kept on its error and no-data states.
const MediaList = compose(
	withErrorAndNoDataComponents,
	withQueuedPhotoStates,
	ListContainerNew,
)(MediaListRaw);

export default connect(null, mapDispatchToProps)(MediaList);
